import { ScheduleInfo } from '../types/schedule.types';
import { SceneWithUserNames } from '../types/scene.types';
import { ElementInfo } from '../types/element.types';
import { SortConfig } from '../pages/Breakdown/BreakdownMain/BreakdownMain.types';

/** 与 ScenesTable 可排序列 key 一致 */
export const SCENES_TABLE_SORTABLE_KEYS = [
  'sceneCode',
  'sceneSet',
  'sceneLocation',
  'scenePeriod',
] as const;

function normalizeScenesTableSortDirection(raw: string): 'asc' | 'desc' | null {
  const s = raw.trim().toLowerCase();
  if (s === 'asc' || s === '正序') return 'asc';
  if (s === 'desc' || s === '倒序') return 'desc';
  return null;
}

/**
 * 解析本地记忆的场次表排序：`breakdownId;列key;asc|desc`（或 正序/倒序）
 */
export function parseScenesTableSortForBreakdownId(
  entries: string[] | null | undefined,
  breakdownId: string
): SortConfig | null {
  if (!entries?.length || !breakdownId) return null;
  const match = entries.find((item) => {
    const parts = item.split(';');
    return parts.length >= 3 && parts[0] === breakdownId;
  });
  if (!match) return null;
  const parts = match.split(';');
  const key = parts[1]?.trim();
  const direction = normalizeScenesTableSortDirection(parts[2] || '');
  if (!key || !direction) return null;
  if (!SCENES_TABLE_SORTABLE_KEYS.includes(key as (typeof SCENES_TABLE_SORTABLE_KEYS)[number])) {
    return null;
  }
  return { key, direction };
}

/**
 * 解析 `breakdownId;value` 格式的 userLastUsed 数组项
 */
export function parseScopedUserLastUsedEntry(
  entries: string[] | null | undefined,
  breakdownId: string
): string | null {
  if (!entries?.length) return null;
  const match = entries.find((item) => {
    const parts = item.split(';');
    return parts.length >= 2 && parts[0] === breakdownId;
  });
  if (!match) return null;
  const parts = match.split(';');
  return parts[1]?.trim() || null;
}

/**
 * 从 userLastUsedScheduleID 中移除包含指定 scheduleID 的项（任意分场）。
 * 在拍摄计划已删除或 GET /schedule/:id 返回 404 时调用，避免行为记忆仍指向无效 ID。
 */
export function withoutUserLastUsedScheduleEntryByScheduleId(
  entries: string[] | null | undefined,
  scheduleId: string
): string[] | null {
  if (!entries?.length || !scheduleId?.trim()) return entries?.length ? [...entries] : null;
  const sid = scheduleId.trim();
  const filtered = entries.filter((item) => {
    const parts = item.split(';');
    return !(parts.length >= 2 && parts[1]?.trim() === sid);
  });
  return filtered.length > 0 ? filtered : null;
}

/**
 * 在拍摄计划列表中选取「当前拍摄计划」ID：
 * 仅使用服务端行为记忆（userLastUsedScheduleID）且必须仍在列表中；不做任何兜底选择。
 */
export function resolveEffectiveScheduleId(
  userLastUsedScheduleID: string[] | null | undefined,
  breakdownId: string,
  schedulesList: ScheduleInfo[]
): string | null {
  if (!schedulesList.length) return null;
  const idSet = new Set(schedulesList.map((p) => p.scheduleID).filter(Boolean) as string[]);
  const fromUser = parseScopedUserLastUsedEntry(userLastUsedScheduleID, breakdownId);
  if (fromUser && idSet.has(fromUser)) {
    return fromUser;
  }
  return null;
}

/**
 * 解析当前分场下应选中的场次 ID：优先服务端记忆且仍在列表中，否则第一场
 */
export function resolveEffectiveSceneId(
  userLastUsedSceneID: string[] | null | undefined,
  breakdownId: string,
  scenes: SceneWithUserNames[]
): string | null {
  if (!scenes.length) return null;
  const idSet = new Set(scenes.map((s) => s.sceneID).filter(Boolean));
  const fromUser = parseScopedUserLastUsedEntry(userLastUsedSceneID, breakdownId);
  if (fromUser && idSet.has(fromUser)) {
    return fromUser;
  }
  return scenes[0]?.sceneID ?? null;
}

/**
 * 解析当前分场下应选中的场次元素 ID：优先服务端记忆且仍在列表中，否则第一项
 */
export function resolveEffectiveElementId(
  userLastUsedElementID: string[] | null | undefined,
  breakdownId: string,
  elements: ElementInfo[]
): string | null {
  if (!elements.length) return null;
  const idSet = new Set(elements.map((i) => i.elementID).filter(Boolean) as string[]);
  const fromUser = parseScopedUserLastUsedEntry(userLastUsedElementID, breakdownId);
  if (fromUser && idSet.has(fromUser)) {
    return fromUser;
  }
  return elements[0]?.elementID ?? null;
}
